// ギフティング画面の純関数ルール（Step 0 で凍結・scripts/test-gifting-rules.mjs で検証）。
// 設計正本=tmp/fable/2026-10-05-gifting-console/plan.md §2-1・§3-3・§3-4。
// サーバー側の正本は gifting-harness src/lib/stage.ts（段階遷移）。ここは表示・判定用の写し。
import type {
  AffAffiliate,
  GfActionKey,
  GfActionTier,
  GfAutoDm,
  GfCampaign,
  GfCampaignType,
  GfContactedInput,
  GfCreator,
  GfDealStatus,
  GfEntry,
  GfIgDetectedMedia,
  GfIgDetectStatus,
  GfPlatform,
  GfPost,
  GfReuseState,
  GfSns,
  GfStage,
  GfStageSource,
} from "../types/gifting";

/** 第1弾=1（出荷は手動で追跡入力）。第2弾=2（住所受領→「出荷指示を送る」1クリック→追跡は同期で取り込む）。 */
export const GF_PHASE: 1 | 2 = 2;

export const GF_ORIGIN = "https://gift.nayu.site";

// ---- 段階 -----------------------------------------------------------------------

export const STAGE_NAMES: readonly string[] = [
  "リスト化",
  "打診中",
  "返信待ち",
  "条件合意",
  "住所回収",
  "発送済",
  "投稿確認",
  "2次利用許諾",
  "アーカイブ",
];

/** 7セグメント進捗バーの対象段階（1〜7） */
export const BAR_STAGES: readonly GfStage[] = [1, 2, 3, 4, 5, 6, 7];

/** パネルのタイムラインで「未到達の自動段階」に出す出所 */
export const STAGE_AUTO_SOURCE: Partial<Record<GfStage, string>> = {
  4: "本人フォーム",
  5: GF_PHASE >= 2 ? "OMS 追跡" : "",
  6: "本人フォーム",
};

export const SOURCE_LABEL: Record<GfStageSource, string> = {
  manual: "手動",
  form: "本人フォーム",
  oms: "OMS 追跡",
  ig_api: "Instagram API",
  system: "システム",
  legacy: "移行",
};

export interface BoardColumnDef {
  key: "nego" | "ship" | "post";
  name: string;
  sub: string;
  range: [GfStage, GfStage];
  waitLabel: string;
}

export const BOARD_COLUMNS: readonly BoardColumnDef[] = [
  { key: "nego", name: "交渉中", sub: "打診 → 返信 → 条件合意", range: [1, 3], waitLabel: "返信待ち" },
  {
    key: "ship",
    name: "配送中",
    sub: GF_PHASE >= 2 ? "住所回収 → 発送（フォーム・OMS連動）" : "住所回収 → 発送（フォーム連動）",
    range: [4, 5],
    waitLabel: "進行中",
  },
  { key: "post", name: "投稿・許諾", sub: "投稿 → 確認 → 2次利用許諾", range: [6, 7], waitLabel: "投稿待ち" },
];

/** アーカイブ扱い（カンバン・一覧から外す）: 段階8 または中止 */
export function isArchived(e: Pick<GfEntry, "stage" | "status">): boolean {
  return e.stage >= 8 || e.status === "cancelled";
}

/**
 * status 直書き時の逆同期（plan.md §2-1）。サーバー stage.ts と同じ規則。
 * draft→min(cur,3)、waiting_address/address_received→4、shipped→5、posted→cur>=6?cur:6、completed→8、cancelled→cur
 */
export function stageFromStatus(status: GfDealStatus, cur: GfStage): GfStage {
  switch (status) {
    case "draft":
      return (cur >= 1 && cur <= 3 ? cur : 3) as GfStage;
    case "waiting_address":
    case "address_received":
      return 4;
    case "shipped":
      return 5;
    case "posted":
      return cur >= 6 && cur <= 7 ? cur : 6;
    case "completed":
      return 8;
    case "cancelled":
      return cur;
  }
}

/** 段階→派生 status（段階4 の副状態は呼び出し側が渡す） */
export function statusForStage(stage: GfStage, addressReceived = false): GfDealStatus {
  if (stage <= 3) return "draft";
  if (stage === 4) return addressReceived ? "address_received" : "waiting_address";
  if (stage === 5) return "shipped";
  if (stage <= 7) return "posted";
  return "completed";
}

/** 段階を進めた先（6→8 直行は reuse_required=0）。7/8 以外の上限は8 */
export function nextStage(e: Pick<GfEntry, "stage">, campaign: Pick<GfCampaign, "reuse_required"> | null): GfStage {
  if (e.stage === 6 && campaign && campaign.reuse_required === 0) return 8;
  return Math.min(e.stage + 1, 8) as GfStage;
}

// ---- 時刻・経過日数 ------------------------------------------------------------------

const DAY_MS = 86_400_000;

/** サーバーの時刻文字列を Date に。'YYYY-MM-DD HH:MM:SS'（SQLite datetime・UTC）と ISO の両方を受ける */
export function parseServerTime(s: string | null | undefined): Date | null {
  if (!s) return null;
  let t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) t += "T00:00:00";
  else if (!t.includes("T")) t = t.replace(" ", "T");
  if (!/([zZ]|[+-]\d\d:?\d\d)$/.test(t)) t += "Z";
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 経過日数 = floor((now − 基準)/1日)。基準 = stage_changed_at と（段階1〜3なら）last_contact_at の新しい方。
 * 基準が無ければ created_at。
 */
export function ageDays(
  e: Pick<GfEntry, "stage" | "stage_changed_at" | "last_contact_at" | "created_at">,
  nowIso: string
): number {
  const now = parseServerTime(nowIso)?.getTime() ?? Date.now();
  const cands = [parseServerTime(e.stage_changed_at)];
  if (e.stage >= 1 && e.stage <= 3) cands.push(parseServerTime(e.last_contact_at));
  const base = cands.filter((d): d is Date => !!d).reduce<number | null>((m, d) => (m === null || d.getTime() > m ? d.getTime() : m), null)
    ?? parseServerTime(e.created_at)?.getTime()
    ?? now;
  return Math.max(0, Math.floor((now - base) / DAY_MS));
}

export function ageLabel(days: number): string {
  return days === 0 ? "今日" : `${days}日経過`;
}

/** 4日以上は赤（段階7以上は除く） */
export function ageIsLate(days: number, stage: GfStage): boolean {
  return days >= 4 && stage < 7;
}

const JST_FMT_MD = new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", month: "numeric", day: "numeric" });
const JST_FMT_YMD = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo" });
const JST_FMT_DT = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** 'M/D'（JST）。無ければ '' */
export function fmtMD(s: string | null | undefined): string {
  const d = parseServerTime(s);
  if (!d) return "";
  const parts = JST_FMT_MD.formatToParts(d);
  const m = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  return `${m}/${day.padStart(2, "0")}`;
}

/** 'YYYY-MM-DD'（JST） */
export function fmtYmd(s: string | null | undefined): string {
  const d = parseServerTime(s);
  return d ? JST_FMT_YMD.format(d) : "";
}

/** 'M/D HH:MM'（JST） */
export function fmtDateTime(s: string | null | undefined): string {
  const d = parseServerTime(s);
  return d ? JST_FMT_DT.format(d) : "";
}

/** 'YYYY-MM'（JST） */
export function jstMonthOf(nowIso: string): string {
  const d = parseServerTime(nowIso) ?? new Date();
  return JST_FMT_YMD.format(d).slice(0, 7);
}

/** 'YYYY年M月D日（曜）時点' */
export function fmtAsOf(nowIso: string): string {
  const d = parseServerTime(nowIso) ?? new Date();
  const s = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(d);
  return `${s.replace(/\((.)\)/, "（$1）")}時点`;
}

// ---- v2 次のアクション（要対応ファースト・2026-10-30）----------------------------------------
// 設計正本=tmp/fable/2026-10-30-gifting-ui-v2/plan.md §1・design/handoff-README.md §1。
// 1エントリーに「次のアクション」を1つだけ割り当て、色（ティア）と並び順もここから決める。

export const DEFAULT_DEADLINE_DAYS = 60;
/** 発送からこの日数たっても投稿・連絡がなければリマインド */
export const REMIND_AFTER_DAYS = 45;
/** リマインドを送ってからこの日数は再びリマインド対象にしない */
export const REMIND_COOLDOWN_DAYS = 14;
/** 返信待ちでこの日数たてば催促 */
export const NUDGE_AFTER_DAYS = 4;
/** 住所入力待ちでこの日数たてば住所を催促 */
export const ADDR_NUDGE_AFTER_DAYS = 3;

export type GfActionGroup = "top" | "due" | "routine" | "wait";

export interface GfActionMeta {
  key: GfActionKey;
  /** チップ・一覧のボタン */
  label: string;
  /** パネルの主ボタン */
  cta: string;
  tier: GfActionTier;
  group: GfActionGroup;
  /** ダッシュボード・凡例の一言 */
  desc: string;
}

export const ACTION_META: Record<GfActionKey, GfActionMeta> = {
  reply: { key: "reply", label: "返信する", cta: "返信する", tier: 0, group: "top", desc: "相手が待っています。最優先で返信" },
  ship: { key: "ship", label: "出荷指示", cta: "出荷指示を送る", tier: 1, group: "top", desc: "住所が届きました。今すぐ出荷指示を" },
  fix: { key: "fix", label: "住所を修正", cta: "住所を修正", tier: 1, group: "top", desc: "出荷チェックで書式エラー。修正してから出荷指示" },
  postdue: { key: "postdue", label: "投稿を催促", cta: "投稿を催促する", tier: 2, group: "due", desc: "投稿期限を過ぎて未投稿" },
  nudge: { key: "nudge", label: "催促", cta: "催促する", tier: 2, group: "due", desc: `返信待ち${NUDGE_AFTER_DAYS}日以上` },
  addrnudge: { key: "addrnudge", label: "住所催促", cta: "住所を催促する", tier: 2, group: "due", desc: `住所入力待ち${ADDR_NUDGE_AFTER_DAYS}日以上` },
  remind: { key: "remind", label: "リマインド", cta: "リマインドを送る", tier: 3, group: "due", desc: `発送から${REMIND_AFTER_DAYS}日・連絡なし。定型文で一言` },
  confirm: { key: "confirm", label: "投稿確認", cta: "投稿を確認する", tier: 4, group: "routine", desc: "投稿の内容と PR 表記を確認" },
  terms: { key: "terms", label: "条件を確定", cta: "住所フォームを送る", tier: 4, group: "routine", desc: "条件が決まった。住所フォームを送る" },
  pitch: { key: "pitch", label: "打診する", cta: "打診した（記録）", tier: 4, group: "routine", desc: "最初の声かけ" },
  license: { key: "license", label: "許諾を記録", cta: "2次利用許諾を記録してアーカイブ", tier: 4, group: "routine", desc: "2次利用の許可を記録" },
  wait: { key: "wait", label: "待ち", cta: "", tier: 5, group: "wait", desc: "こちらのアクション不要" },
};

/** 「今日やること」チップのグループと順（handoff-README ①b） */
export const ACTION_GROUPS: ReadonlyArray<{ group: GfActionGroup; label: string; keys: readonly GfActionKey[] }> = [
  { group: "top", label: "最優先", keys: ["reply", "ship", "fix"] },
  { group: "due", label: "期限", keys: ["postdue", "nudge", "addrnudge", "remind"] },
  { group: "routine", label: "定常", keys: ["confirm", "terms", "pitch", "license"] },
];

export const ACTION_KEYS: readonly GfActionKey[] = [...ACTION_GROUPS.flatMap((g) => g.keys), "wait"];

export function isActionKey(s: string | null | undefined): s is GfActionKey {
  return !!s && (ACTION_KEYS as readonly string[]).includes(s);
}

/** floor((now − from)/1日)。解釈できなければ null */
export function daysBetween(fromIso: string | null | undefined, nowIso: string): number | null {
  const from = parseServerTime(fromIso);
  if (!from) return null;
  const now = parseServerTime(nowIso)?.getTime() ?? Date.now();
  return Math.floor((now - from.getTime()) / DAY_MS);
}

/** 複数の時刻のうち最新（文字列比較ではなく時刻で比較） */
export function latestIso(...xs: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestT = -Infinity;
  for (const x of xs) {
    const d = parseServerTime(x);
    if (d && d.getTime() > bestT) {
      bestT = d.getTime();
      best = x ?? null;
    }
  }
  return best;
}

/** 最終送信 = max(IG 送信, 手動記録 last_contact_at) */
export function lastOutAt(e: Pick<GfEntry, "last_out_at" | "last_contact_at">): string | null {
  return latestIso(e.last_out_at, e.last_contact_at);
}

/** 最終連絡（送受信どちらも） */
export function lastContactAny(e: Pick<GfEntry, "last_out_at" | "last_contact_at" | "last_in_at">): string | null {
  return latestIso(e.last_out_at, e.last_contact_at, e.last_in_at);
}

/** 未対応の受信DM（IG連携済・最終受信 > 最終送信）。手動記録（last_contact_at）でも解除される */
export function hasUnreplied(e: Pick<GfEntry, "ig_linked" | "last_in_at" | "last_out_at" | "last_contact_at">): boolean {
  if (!e.ig_linked || !e.last_in_at) return false;
  const inT = parseServerTime(e.last_in_at)?.getTime() ?? 0;
  const outT = parseServerTime(lastOutAt(e))?.getTime() ?? 0;
  return inT > outT;
}

export interface GfDeadlineInfo {
  /** 'YYYY-MM-DD' */
  dueYmd: string;
  /** 期限日 − 今日（JST・暦日）。負なら超過 */
  daysLeft: number;
  /** 期限日数（post_deadline − shipped_at）。発送日が無ければ null */
  days: number | null;
  overdue: boolean;
  /** 発送からの日数（発送日が無ければ null） */
  sinceShip: number | null;
}

function ymdToUtc(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Date.UTC(y, (m ?? 1) - 1, d ?? 1);
}

/** 制作期限（投稿期限）。post_deadline が無ければ null */
export function deadlineInfo(e: Pick<GfEntry, "post_deadline" | "shipped_at">, nowIso: string): GfDeadlineInfo | null {
  const dueYmd = e.post_deadline ? fmtYmd(e.post_deadline) || e.post_deadline.slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueYmd)) return null;
  const todayYmd = fmtYmd(nowIso);
  const daysLeft = Math.round((ymdToUtc(dueYmd) - ymdToUtc(todayYmd)) / DAY_MS);
  const shipYmd = e.shipped_at ? fmtYmd(e.shipped_at) : "";
  const days = shipYmd ? Math.round((ymdToUtc(dueYmd) - ymdToUtc(shipYmd)) / DAY_MS) : null;
  const sinceShip = shipYmd ? Math.round((ymdToUtc(todayYmd) - ymdToUtc(shipYmd)) / DAY_MS) : null;
  return { dueYmd, daysLeft, days, overdue: daysLeft < 0, sinceShip };
}

/** 'YYYY-MM-DD' に日数を足す */
export function addDaysYmd(ymd: string, days: number): string {
  return new Date(ymdToUtc(ymd) + days * DAY_MS).toISOString().slice(0, 10);
}

export interface GfNextAction extends GfActionMeta {
  /** 並び順（小さいほど上） */
  sortKey: number;
}

/**
 * 次のアクション（上から順に最初に当たったもの・plan.md §1）。
 * 0 アーカイブ/中止 → wait／1 reply／2 ship,fix／3 postdue／4 remind／5 nudge／6 addrnudge／7 pitch,terms,confirm,license／8 wait
 */
export interface NextActionOpts {
  /** 同じ人の別案件に reply を付けたので、この案件では未返信を無視する（nextActionsOf が使う） */
  suppressReply?: boolean;
}

export function nextAction(
  e: GfEntry,
  campaign: Pick<GfCampaign, "reuse_required"> | null,
  nowIso: string,
  opts: NextActionOpts = {}
): GfNextAction {
  const meta = (k: GfActionKey): GfActionMeta => ACTION_META[k];
  const finish = (m: GfActionMeta): GfNextAction => ({ ...m, sortKey: actionSortKey(e, m, nowIso) });
  if (isArchived(e)) return finish(meta("wait"));
  if (!opts.suppressReply && hasUnreplied(e)) return finish(meta("reply"));
  if (e.stage === 4 && e.status === "address_received" && !e.ship_order_no) {
    return finish(meta(e.ship_validation && !e.ship_validation.ok ? "fix" : "ship"));
  }
  if (e.stage === 5 && e.post_count === 0) {
    const dl = deadlineInfo(e, nowIso);
    if (dl && dl.overdue) return finish(meta("postdue"));
    const sinceShip = dl?.sinceShip ?? daysBetween(e.shipped_at, nowIso);
    const sinceContact = daysBetween(lastContactAny(e), nowIso) ?? Infinity;
    const sinceRemind = daysBetween(e.last_reminder_at, nowIso);
    if (
      sinceShip !== null &&
      sinceShip >= REMIND_AFTER_DAYS &&
      sinceContact > REMIND_AFTER_DAYS &&
      (sinceRemind === null || sinceRemind >= REMIND_COOLDOWN_DAYS)
    ) {
      return finish(meta("remind"));
    }
  }
  if (e.stage === 2) {
    const since = daysBetween(lastOutAt(e) ?? e.stage_changed_at, nowIso) ?? ageDays(e, nowIso);
    if (since >= NUDGE_AFTER_DAYS) return finish(meta("nudge"));
  }
  if (e.stage === 4 && e.status === "waiting_address") {
    const waiting = ageDays(e, nowIso);
    const sinceOut = daysBetween(lastOutAt(e), nowIso) ?? waiting;
    if (Math.min(waiting, sinceOut) >= ADDR_NUDGE_AFTER_DAYS) return finish(meta("addrnudge"));
  }
  if (e.stage === 1) return finish(meta("pitch"));
  if (e.stage === 3) return finish(meta("terms"));
  if (e.stage === 6) {
    const m = meta("confirm");
    return finish(campaign && campaign.reuse_required === 0 ? { ...m, cta: "投稿を確認してアーカイブ" } : m);
  }
  if (e.stage === 7) return finish(meta("license"));
  return finish(meta("wait"));
}

/** tier×10000＋補助キー（handoff-README「並び順」）。小さいほど上 */
export function actionSortKey(e: GfEntry, a: Pick<GfActionMeta, "key" | "tier">, nowIso: string): number {
  let s: number;
  const dl = deadlineInfo(e, nowIso);
  switch (a.key) {
    case "reply":
      s = daysBetween(e.last_in_at, nowIso) ?? 0;
      break;
    case "postdue":
    case "remind":
      s = dl?.daysLeft ?? 0;
      break;
    case "nudge":
      s = -(daysBetween(lastOutAt(e) ?? e.stage_changed_at, nowIso) ?? 0);
      break;
    case "addrnudge":
      s = -ageDays(e, nowIso);
      break;
    default:
      s = a.tier === 5 && e.stage === 5 && dl ? dl.daysLeft : -ageDays(e, nowIso);
  }
  return a.tier * 10000 + Math.max(-4999, Math.min(4999, s)) + 5000;
}

/** 案件の「直近に動いた時刻」（同じ人の複数案件のうち、どれに返信を紐づけるかの基準） */
function lastActivityAt(e: GfEntry): number {
  return parseServerTime(latestIso(e.stage_changed_at, e.last_contact_at, e.updated_at, e.created_at))?.getTime() ?? 0;
}

/**
 * 全エントリーの次のアクションを一括で（Map: deal id → action）。一覧・カンバン・パネル・ダッシュボードは必ずこれを使う。
 * 受信DMは人単位（ig_dm_state）なので、同じ人が複数案件を進行中だと全案件が「返信する」になる。
 * ここでは直近に動いた1案件だけに reply を付け、残りは未返信を無視して通常の判定にする（件数の水増しを防ぐ）。
 */
export function nextActionsOf(entries: readonly GfEntry[], campaignsById: Map<string, GfCampaign>, nowIso: string): Map<string, GfNextAction> {
  const campaignOf = (e: GfEntry) => (e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null);
  // 人ごとに「未返信で進行中」の案件を集め、直近に動いた1件を選ぶ
  const replyOwner = new Map<string, string>();
  const byPerson = new Map<string, GfEntry[]>();
  for (const e of entries) {
    if (isArchived(e) || !hasUnreplied(e)) continue;
    const list = byPerson.get(e.influencer_id) ?? [];
    list.push(e);
    byPerson.set(e.influencer_id, list);
  }
  for (const [pid, list] of byPerson) {
    const best = [...list].sort((a, b) => lastActivityAt(b) - lastActivityAt(a) || a.stage - b.stage)[0];
    if (best) replyOwner.set(pid, best.id);
  }
  const m = new Map<string, GfNextAction>();
  for (const e of entries) {
    const owner = replyOwner.get(e.influencer_id);
    m.set(e.id, nextAction(e, campaignOf(e), nowIso, { suppressReply: owner !== undefined && owner !== e.id }));
  }
  return m;
}

/** アクションボタンから開く作成欄の種類（手動記録 /contacted の kind） */
export function contactKindOf(key: GfActionKey | "dm", stage: GfStage): NonNullable<GfContactedInput["kind"]> {
  switch (key) {
    case "pitch":
      return "pitch";
    case "remind":
    case "postdue":
      return "reminder";
    case "nudge":
    case "addrnudge":
      return stage === 5 ? "reminder" : "nudge";
    default:
      return "dm";
  }
}

/** 作成欄を開くアクション（それ以外は即実行／パネルを開くだけ） */
export const COMPOSE_ACTIONS: readonly GfActionKey[] = ["reply", "remind", "nudge", "addrnudge", "postdue", "pitch"];

export interface ComposeCtx {
  name: string;
  campaignName: string;
  product: string;
  formUrl: string | null;
  /** 'YYYY-MM-DD' */
  deadlineYmd: string | null;
}

function fmtYmdJa(ymd: string | null): string {
  if (!ymd) return "";
  const [, m, d] = ymd.split("-");
  return `${Number(m)}月${Number(d)}日`;
}

/** アクション別の定型文（送る前に人が直す前提。報酬額・効果の断定は入れない） */
export function composeText(key: GfActionKey, ctx: ComposeCtx): string {
  const due = fmtYmdJa(ctx.deadlineYmd);
  switch (key) {
    case "reply":
      return `${ctx.name}さん、ご返信ありがとうございます！条件をご案内いたします。`;
    case "remind":
      return `お送りした${ctx.product || "商品"}はいかがでしたか。投稿の目安は${due ? ` ${due}` : "発送から2か月以内"}までとなります。ご状況をお聞かせください。`;
    case "nudge":
      return "先日お送りしたご案内の件、ご検討いかがでしょうか。";
    case "addrnudge":
      return `お届け先のご入力をお願いいたします。${ctx.formUrl ? `フォーム：${ctx.formUrl}` : ""}`.trim();
    case "postdue":
      return `投稿の目安（${due || "発送から2か月"}）を過ぎておりますが、ご状況はいかがでしょうか。`;
    case "pitch":
      return `はじめまして、STOQです。${ctx.campaignName ? `${ctx.campaignName}の` : ""}ギフティングのご相談でご連絡しました。`;
    default:
      return "";
  }
}

// ---- 要対応判定（互換。中身は nextAction から導出） ----------------------------------------------

export type ActionTone = "warn" | "caution" | "info" | "ok";

export interface EntryAction {
  /** カード右上の短いラベル（例: 催促） */
  label: string;
  /** パネル主ボタンの文言 */
  cta: string;
  tone: ActionTone;
  key: GfActionKey;
  tier: GfActionTier;
}

const TIER_TONE: Record<GfActionTier, ActionTone> = { 0: "info", 1: "warn", 2: "warn", 3: "caution", 4: "info", 5: "ok" };

/** 要対応なら EntryAction、待ちなら null（v2: nextAction の写し） */
export function entryAction(
  e: GfEntry,
  campaign: Pick<GfCampaign, "reuse_required"> | null,
  nowIso: string
): EntryAction | null {
  const a = nextAction(e, campaign, nowIso);
  if (a.key === "wait") return null;
  return { label: a.label, cta: a.cta, tone: TIER_TONE[a.tier], key: a.key, tier: a.tier };
}

/** 待ちカードやパネルで使う「次にやること」の文言（要対応でない時も出す） */
export function nextLabel(e: GfEntry, campaign: Pick<GfCampaign, "reuse_required"> | null, nowIso: string): string {
  const a = entryAction(e, campaign, nowIso);
  if (a) return a.cta;
  if (e.status === "cancelled") return "中止";
  switch (e.stage) {
    case 2:
      return "返信を待つ";
    case 4:
      return e.status === "address_received" ? "出荷指示済・追跡待ち" : "住所の入力を待つ";
    case 5:
      return "投稿を待つ";
    case 8:
      return "完了";
    default:
      return "";
  }
}

export interface BoardCard {
  entry: GfEntry;
  action: EntryAction | null;
  age: number;
}

export interface BoardColumn extends BoardColumnDef {
  act: BoardCard[];
  wait: BoardCard[];
  count: number;
}

/** カンバン列の組み立て。要対応は経過日数の降順、待ちは段階昇順（同段階は経過降順） */
export function buildBoardColumns(
  entries: GfEntry[],
  campaignsById: Map<string, GfCampaign>,
  nowIso: string,
  campaignFilter: string
): BoardColumn[] {
  const live = entries.filter((e) => !isArchived(e) && (campaignFilter === "all" || e.campaign_id === campaignFilter));
  return BOARD_COLUMNS.map((col) => {
    const cards = live
      .filter((e) => e.stage >= col.range[0] && e.stage <= col.range[1])
      .map<BoardCard>((e) => {
        const c = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
        return { entry: e, action: entryAction(e, c, nowIso), age: ageDays(e, nowIso) };
      });
    const act = cards.filter((c) => c.action).sort((a, b) => b.age - a.age);
    const wait = cards.filter((c) => !c.action).sort((a, b) => a.entry.stage - b.entry.stage || b.age - a.age);
    return { ...col, act, wait, count: cards.length };
  });
}

/** 営業リスト（段階0）: 進行中エントリーが無く、status が candidate のクリエイター */
export function listCandidates(creators: GfCreator[], entries: GfEntry[]): GfCreator[] {
  const busy = new Set(entries.filter((e) => !isArchived(e)).map((e) => e.influencer_id));
  return creators.filter((c) => c.status === "candidate" && !busy.has(c.id));
}

// ---- 案件タイプ・料率・条件 -------------------------------------------------------------

export const CAMPAIGN_TYPE_LABEL: Record<GfCampaignType, string> = {
  gift: "無償",
  paid: "有償",
  gift_affiliate: "無償＋アフィリ",
  paid_affiliate: "有償＋アフィリ",
};

export const CAMPAIGN_TYPES: readonly GfCampaignType[] = ["gift", "paid", "gift_affiliate", "paid_affiliate"];

export function typeHasFee(t: GfCampaignType): boolean {
  return t === "paid" || t === "paid_affiliate";
}

export function typeHasAffiliate(t: GfCampaignType): boolean {
  return t === "gift_affiliate" || t === "paid_affiliate";
}

/**
 * エントリーの有効な報酬タイプ（v3・2026-11-02）: エントリー個別の上書き（terms_type）＞ 案件（campaign.type）＞ 旧 deal_type。
 * サーバー側（gifting-harness lib/deal-terms.ts effectiveTermsType）と同じ規則。
 */
export function effectiveType(
  e: Pick<GfEntry, "deal_type"> & Partial<Pick<GfEntry, "terms_type">>,
  campaign: Pick<GfCampaign, "type"> | null | undefined
): GfCampaignType {
  if (e.terms_type) return e.terms_type;
  if (campaign) return campaign.type;
  return e.deal_type === "paid" ? "paid" : "gift";
}

export interface ResolvedRate {
  /** 小数（0.12）。アフィリ対象外/未登録は null */
  rate: number | null;
  label: string;
  src: "個別" | "案件" | "アフィリなし" | "未登録";
}

/**
 * 料率の解決（plan.md §0-3）。正本は affiliate の commission_rate。
 * アフィリ無し案件→「—」。affiliate 未登録→案件料率を「未登録」として表示（成果は計上されない）。
 * commission_rate ≠ base_rate → 個別、= → 案件。
 */
export function resolveRate(
  affiliate: Pick<AffAffiliate, "commission_rate"> | null | undefined,
  campaign: Pick<GfCampaign, "type" | "base_rate"> | null,
  /** v3: エントリー個別の有効タイプ（effectiveType）。省略時は案件のタイプ */
  type: GfCampaignType | null = campaign?.type ?? null
): ResolvedRate {
  if (!type || !typeHasAffiliate(type)) return { rate: null, label: "—", src: "アフィリなし" };
  const base = campaign?.base_rate ?? 0;
  if (!affiliate) return { rate: null, label: `${fmtPct(base)}（未登録）`, src: "未登録" };
  const r = affiliate.commission_rate;
  if (Math.abs(r - base) < 1e-9) return { rate: r, label: fmtPct(r), src: "案件" };
  return { rate: r, label: fmtPct(r), src: "個別" };
}

/** gifting influencer id → affiliate（gifting_influencer_id で突合） */
export function affiliateByInfluencer(affiliates: AffAffiliate[]): Map<string, AffAffiliate> {
  const m = new Map<string, AffAffiliate>();
  for (const a of affiliates) if (a.gifting_influencer_id && a.status !== "archived") m.set(a.gifting_influencer_id, a);
  return m;
}

/** カードの条件表記（例: ¥5万 ＋ 15%） */
export function termsLabel(
  e: Pick<GfEntry, "fee_amount" | "deal_type"> & Partial<Pick<GfEntry, "terms_type">>,
  campaign: GfCampaign | null,
  rate: ResolvedRate
): string {
  const parts: string[] = [];
  const fee = e.fee_amount ?? campaign?.fee_amount ?? 0;
  if (fee > 0 && typeHasFee(effectiveType(e, campaign))) parts.push(fmtYenMan(fee));
  if (rate.rate !== null) parts.push(fmtPct(rate.rate));
  return parts.join(" ＋ ") || "—";
}

/** 試算: 固定報酬 + 想定売上 × 料率 */
export function estimatePayout(fee: number, rate: number | null, simSales: number): number {
  return Math.round(fee + (rate ?? 0) * simSales);
}

// ---- 整形 ---------------------------------------------------------------------------

export function fmtYen(n: number | null | undefined): string {
  return `¥${Math.round(n ?? 0).toLocaleString("ja-JP")}`;
}

/** ¥5万 / ¥12万 / ¥8,000（1万未満はそのまま） */
export function fmtYenMan(n: number): string {
  if (n >= 10_000 && n % 1000 === 0) {
    const man = n / 10_000;
    return `¥${Number.isInteger(man) ? man : man.toFixed(1)}万`;
  }
  return fmtYen(n);
}

/** ¥3.92M / ¥888K（KPI 用） */
export function fmtYenShort(n: number): string {
  if (n >= 1_000_000) return `¥${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `¥${Math.round(n / 1000).toLocaleString("ja-JP")}K`;
  return fmtYen(n);
}

/** 0.12 → '12%'、0.085 → '8.5%' */
export function fmtPct(r: number): string {
  const v = Math.round(r * 1000) / 10;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}%`;
}

/** フォロワー数 → '12.4万' / '9,800' */
export function fmtFollowers(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 10_000) {
    const v = Math.round(n / 1000) / 10;
    return `${Number.isInteger(v) ? v : v.toFixed(1)}万`;
  }
  return n.toLocaleString("ja-JP");
}

export const PLATFORM_SHORT: Record<GfPlatform, string> = {
  instagram: "IG",
  youtube: "YT",
  tiktok: "TT",
  ameblo: "AB",
  x: "X",
  threads: "TH",
  other: "他",
};

export const PLATFORM_LABEL: Record<GfPlatform, string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  ameblo: "Ameblo",
  x: "X",
  threads: "Threads",
  other: "その他",
};

/** 媒体チップ表記（Ameblo は PV 表記にしない＝データは follower_count のみのため '万' で統一） */
export function mediaChip(platform: GfPlatform, followers: number | null): string {
  return `${PLATFORM_SHORT[platform]} ${fmtFollowers(followers)}`;
}

/** プロフィール URL（url が無いときの推定） */
export function profileUrl(platform: GfPlatform, handle: string, url: string | null): string | null {
  if (url) return url;
  const h = handle.replace(/^@/, "");
  switch (platform) {
    case "instagram":
      return `https://www.instagram.com/${h}/`;
    case "youtube":
      return `https://www.youtube.com/@${h}`;
    case "tiktok":
      return `https://www.tiktok.com/@${h}`;
    case "ameblo":
      return `https://ameblo.jp/${h}/`;
    case "x":
      return `https://x.com/${h}`;
    case "threads":
      return `https://www.threads.net/@${h}`;
    default:
      return null;
  }
}

/** 主媒体（is_primary 優先→フォロワー最大） */
export function primarySns<T extends { is_primary: number; follower_count: number | null }>(sns: T[]): T | null {
  if (!sns.length) return null;
  return [...sns].sort((a, b) => b.is_primary - a.is_primary || (b.follower_count ?? -1) - (a.follower_count ?? -1))[0];
}

export function initialOf(name: string): string {
  const ch = Array.from(name.trim())[0] ?? "?";
  return ch.toUpperCase();
}

export function handleOf(sns: { handle: string } | null): string {
  if (!sns) return "";
  return sns.handle.startsWith("@") ? sns.handle : `@${sns.handle}`;
}

// ---- 2次利用 ------------------------------------------------------------------------

export const REUSE_STATE_LABEL: Record<GfReuseState, string> = {
  granted: "許諾済",
  expiring: "期限間近",
  expired: "期限切れ",
  requested: "申請中",
  none: "未取得",
};

/** reuse_allowed=1 → 許諾済（reuse_until 30日以内=期限間近、超過=期限切れ）／reuse_requested_at → 申請中／他=未取得 */
export function reuseState(
  p: Pick<GfPost, "reuse_allowed" | "reuse_until" | "reuse_requested_at">,
  nowIso: string
): GfReuseState {
  if (p.reuse_allowed === 1) {
    if (p.reuse_until) {
      const until = parseServerTime(p.reuse_until.length === 10 ? `${p.reuse_until}T23:59:59+09:00` : p.reuse_until);
      const now = parseServerTime(nowIso);
      if (until && now) {
        const left = until.getTime() - now.getTime();
        if (left < 0) return "expired";
        if (left <= 30 * DAY_MS) return "expiring";
      }
    }
    return "granted";
  }
  if (p.reuse_requested_at) return "requested";
  return "none";
}

export const REUSE_SCOPE_LABEL: Record<string, string> = {
  ad: "広告",
  ec: "EC商品ページ",
  sns: "SNS",
  rakuten_lp: "楽天LP",
  shopify_lp: "Shopify LP",
};

export function reuseScopeLabels(scope: string | null): string[] {
  if (!scope) return [];
  return scope
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => REUSE_SCOPE_LABEL[s] ?? s);
}

// ---- DM テンプレ置換（旧SPA app.js fillTpl の移植） ----------------------------------------

export interface TplContext {
  name: string;
  product: string;
  formUrl: string | null;
  tokenRevoked: boolean;
  deadline: string | null;
}

export function fillTpl(body: string, ctx: TplContext): string {
  return body
    .replace(/\{名前\}/g, ctx.name)
    .replace(/\{商品\}/g, ctx.product)
    .replace(/\{フォームURL\}/g, ctx.formUrl && !ctx.tokenRevoked ? ctx.formUrl : "（フォームURLなし）")
    .replace(/\{期限\}/g, ctx.deadline ? ctx.deadline : "（期限未設定）");
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * fillTpl の逆: 作成欄の文面を「テンプレとして保存」するとき、差し込み済みの名前・商品・URL・期限を変数に戻す。
 * 長い値から順に置換（商品名が名前を含むようなケースで部分置換しない）。空の値は触らない
 */
export function unfillTpl(text: string, ctx: TplContext): string {
  const pairs: Array<[string, string]> = [];
  if (ctx.formUrl && !ctx.tokenRevoked) pairs.push([ctx.formUrl, "{フォームURL}"]);
  pairs.push(["（フォームURLなし）", "{フォームURL}"]);
  if (ctx.deadline) pairs.push([ctx.deadline, "{期限}"]);
  pairs.push(["（期限未設定）", "{期限}"]);
  if (ctx.product.trim()) pairs.push([ctx.product, "{商品}"]);
  if (ctx.name.trim()) pairs.push([ctx.name, "{名前}"]);
  pairs.sort((a, b) => b[0].length - a[0].length);
  let out = text;
  for (const [val, ph] of pairs) out = out.replace(new RegExp(escapeRe(val), "g"), ph);
  return out;
}

/** 出荷内容の検証エラー（第2弾）。stoq-worker の検証文はもともと日本語なのでそのまま、gifting 側のコードだけ日本語にする */
const SHIP_ERROR_LABEL: Record<string, string> = {
  sku_missing: "SKU（商品コード）が未設定です。案件かエントリーに SKU を設定してください",
  not_configured: "倉庫出荷との連携が未設定です",
  scope_violation: "ギフティング以外の出荷は送れません",
  already_instructed: "この出荷指示は送信済みです",
  open_flag_block: "保留フラグがあるため送れません",
  invalid_json: "送信内容が不正です",
};

/** 案件にエントリーを追加できない理由（送る商品が未設定）。null なら追加できる */
export function campaignAddBlocker(
  c: (Pick<GfCampaign, "name" | "product_name"> & Partial<Pick<GfCampaign, "product_mode" | "product_options">>) | null | undefined
): string | null {
  if (!c) return null;
  if (c.product_mode === "choice") {
    return (c.product_options?.length ?? 0) > 0
      ? null
      : `案件「${c.name}」はクリエイターが選ぶセットが1つもありません。「報酬・料率」タブで選択肢を設定してください。`;
  }
  return c.product_name?.trim()
    ? null
    : `案件「${c.name}」の「送る商品」が未設定のため追加できません。「報酬・料率」タブでこの案件の送る商品（とSKU）を設定してください。`;
}

/** 選択待ち（choice 案件で未選択）のときの商品の言い方（DMテンプレの {商品} にも使う） */
export const PRODUCT_PENDING_LABEL = "STOQのセット（フォームでお選びいただけます）";

/** エントリーの「送る商品」の表示名。choice 案件は選ばれたセット名、未選択なら PRODUCT_PENDING_LABEL */
export function entryProductLabel(
  e: Pick<GfEntry, "product_name" | "product_pending">,
  c: Pick<GfCampaign, "product_name" | "product_mode"> | null | undefined
): string {
  if (e.product_pending) return PRODUCT_PENDING_LABEL;
  if (c?.product_mode === "choice") return e.product_name;
  return c?.product_name || e.product_name;
}

export function shipErrorText(errors: readonly string[]): string {
  return errors.map((e) => SHIP_ERROR_LABEL[e] ?? e).join(" / ") || "内容を確認してください";
}

/** Instagram DM の送信上限（gifting /api/ig/friends/:fid/messages と同じ） */
export const DM_MAX_CHARS = 1000;

/** DM 送信に使える Instagram アカウント（ig-harness のフォロワーに紐付け済みのもの）。無ければ null */
export function linkedIgOf<S extends Pick<GfSns, "platform" | "ig_follower_id">>(sns: S[]): (S & { ig_follower_id: number }) | null {
  const s = sns.find((x) => x.platform === "instagram" && x.ig_follower_id != null);
  return s ? (s as S & { ig_follower_id: number }) : null;
}

// ---- 代理入力の住所分割 ---------------------------------------------------------------

export interface ParsedAddress {
  postal: string;
  address: string;
  phone: string;
  name: string;
}

/** 全角英数→半角 */
export function toHalfWidth(s: string): string {
  return s
    .replace(/[０-９Ａ-Ｚａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[－−ー―‐]/g, (c) => (c === "ー" ? c : "-"));
}

/** 郵便番号を 7桁数字に（不正なら ''） */
export function normalizePostal(s: string): string {
  const d = toHalfWidth(s).replace(/[〒\s-]/g, "");
  return /^\d{7}$/.test(d) ? d : "";
}

/** 電話番号を 0始まり10〜11桁に（+81 対応・不正なら ''） */
export function normalizePhone(s: string): string {
  let d = toHalfWidth(s).replace(/[\s()-]/g, "");
  if (d.startsWith("+81")) d = "0" + d.slice(3).replace(/^0/, "");
  return /^0\d{9,10}$/.test(d) ? d : "";
}

/**
 * 貼り付けテキストから 郵便番号・住所・電話・氏名 を推定分割する（DM の文面想定）。
 * 判定できない部分は空文字（UI で手直しする前提）。
 */
export function parsePastedAddress(text: string): ParsedAddress {
  const src = toHalfWidth(text);
  // 電話番号を先に取り除いてから郵便番号を探す（090-1234-5678 の先頭を郵便番号と誤認しないため）
  const phoneM = src.match(/(\+81[\d\s-]{9,14}\d|0\d{1,4}-?\d{1,4}-?\d{3,4})/g);
  const phoneRaw = phoneM ? phoneM.find((p) => normalizePhone(p)) ?? "" : "";
  const phone = phoneRaw ? normalizePhone(phoneRaw) : "";
  const noPhone = phoneRaw ? src.split(phoneRaw).join("\n") : src;
  const postalM = noPhone.match(/〒\s*(\d{3})-?(\d{4})/) ?? noPhone.match(/(?<![\d-])(\d{3})-?(\d{4})(?![\d-])/);
  const postal = postalM ? `${postalM[1]}${postalM[2]}` : "";
  const postalRaw = postalM ? postalM[0] : "";
  const lines = noPhone
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (postalRaw ? l.replace(postalRaw, "") : l).replace(/^(住所|電話番号?|TEL|tel|お名前|氏名|名前)\s*[:：]?\s*/, "").trim())
    .filter(Boolean);
  const PREF = /(北海道|東京都|大阪府|京都府|.{2,3}県)/;
  const addrIdx = lines.findIndex((l) => PREF.test(l));
  // 住所の次の行は「建物名・部屋番号らしい（数字/号/階/室を含み、様で終わらない）」ときだけ住所に含める
  const next = addrIdx >= 0 ? lines[addrIdx + 1] : undefined;
  const nextIsBuilding = !!next && /[\d号階室棟]/.test(next) && !/(様|さま)$/.test(next) && !PREF.test(next);
  const addrLines = addrIdx >= 0 ? [addrIdx, ...(nextIsBuilding ? [addrIdx + 1] : [])] : [];
  const address = addrLines.map((i) => lines[i]).join(" ").trim();
  const nameLine = lines.find((l, i) => !addrLines.includes(i) && !PREF.test(l) && l.length <= 20);
  const name = nameLine ? nameLine.replace(/(様|さま)$/, "").trim() : "";
  return { postal, address, phone, name };
}

// ---- 振込照合用 CSV -----------------------------------------------------------------

export interface PayoutCsvRow {
  name: string;
  holderKana: string;
  bank: string;
  branch: string;
  accountType: string;
  last4: string;
  invoice: string;
  fixed: number;
  affiliate: number;
}

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** 照合用CSV（全銀形式ではない）。UTF-8 BOM 付きで Excel でも文字化けしない */
export function buildPayoutCsv(rows: PayoutCsvRow[], month: string): string {
  const head = ["対象月", "支払先", "口座名義(カナ)", "銀行", "支店", "種別", "口座下4桁", "インボイス", "固定報酬", "アフィリ報酬", "支払額"];
  const body = rows.map((r) =>
    [month, r.name, r.holderKana, r.bank, r.branch, r.accountType, r.last4, r.invoice, r.fixed, r.affiliate, r.fixed + r.affiliate]
      .map(csvCell)
      .join(",")
  );
  return "﻿" + [head.join(","), ...body].join("\r\n") + "\r\n";
}

// ---- 第3弾: Instagram 投稿の自動検知・2次利用の依頼DM自動送信 ------------------------------------

/** 自動送信に使う既定テンプレ（案件の reuse_dm_template_id が null のとき。gifting 側の既定と同じ） */
export const DEFAULT_REUSE_DM_TEMPLATE_ID = "tpl-投稿お礼";

/** 精算・連携タブの「Instagram 投稿検知」カード（発送の OMS カードと同じ作り）。status が取れた後の表示 */
export function igDetectCardOf(s: GfIgDetectStatus): { tone: "ok" | "warn"; label: string; detail: string } {
  if (s.last_error) return { tone: "warn", label: "確認エラー", detail: `直近のエラー: ${s.last_error}` };
  return {
    tone: "ok",
    label: "接続済み",
    detail: `最終確認 ${s.last_sync_at ? fmtDateTime(s.last_sync_at) : "未実行"}・直近7日の自動記録 ${s.counts.linked_7d}件`,
  };
}

/** 検知した投稿の件数内訳（ダッシュボード要対応・素材タブのバッジ用）。review=クリエイター登録済みで案件に紐づいていない／sales=未登録アカウント（営業候補） */
export function igDetectCounts(media: readonly Pick<GfIgDetectedMedia, "status">[]): { review: number; sales: number; total: number } {
  const review = media.filter((m) => m.status === "creator_no_deal").length;
  const sales = media.filter((m) => m.status === "unknown").length;
  return { review, sales, total: review + sales };
}

/** ダッシュボード要対応「検知した投稿の確認 n件」（creator_no_deal のみ。unknown は件数だけ添える）。0件なら null */
export function igDetectTodoOf(media: readonly Pick<GfIgDetectedMedia, "status">[]): { title: string; sub: string; count: number } | null {
  const c = igDetectCounts(media);
  if (c.review === 0) return null;
  return {
    title: `検知した投稿の確認 ${c.review}件`,
    sub: c.sales > 0 ? `案件に紐づけてください（ほか未登録アカウント ${c.sales}件は営業候補）` : "案件に紐づけてください（素材タブ）",
    count: c.review,
  };
}

/** 検知した投稿を紐づけられる案件（そのクリエイターの段階4〜7・中止でない）。段階5（投稿待ち）を先頭に、あとは新しい更新順 */
export function dealsForMedia(
  entries: readonly Pick<GfEntry, "id" | "influencer_id" | "stage" | "status" | "updated_at">[],
  influencerId: string | null
): Array<Pick<GfEntry, "id" | "influencer_id" | "stage" | "status" | "updated_at">> {
  if (!influencerId) return [];
  return entries
    .filter((e) => e.influencer_id === influencerId && e.status !== "cancelled" && e.stage >= 4 && e.stage <= 7)
    .sort((a, b) => (a.stage === 5 ? 0 : 1) - (b.stage === 5 ? 0 : 1) || b.updated_at.localeCompare(a.updated_at));
}

/** 段階6→7 の自動DMの結果をトースト用の1文に。auto_dm が無い（対象外）なら null */
export function autoDmMessage(a: GfAutoDm | null | undefined): string | null {
  if (!a) return null;
  if (a.sent) return "2次利用の依頼DMを自動で送りました";
  switch (a.reason) {
    case "not_linked":
      return "Instagram と未連携のため、2次利用の依頼DMは送れませんでした。手動で依頼してください";
    case "not_configured":
      return "自動送信の設定が未完了のため、2次利用の依頼DMは送れませんでした。手動で依頼してください";
    default:
      return "2次利用の依頼DMの自動送信に失敗しました。履歴を確認し、手動で送ってください（24時間を過ぎると自動送信できません）";
  }
}

/** 自動DMの対象か（案件で自動送信がONかつ2次利用あり）。段階6→7 の確認ダイアログの出し分けに使う */
export function willAutoSendReuseDm(c: Pick<GfCampaign, "reuse_required" | "reuse_dm_auto"> | null | undefined): boolean {
  return !!c && c.reuse_required === 1 && c.reuse_dm_auto === 1;
}

/** ティアの CSS クラス（行の左端バー・面の塗り・チップのドット等。gf-base.css の .gf-tier-N） */
export function tierClass(tier: GfActionTier): string {
  return `gf-tier-${tier}`;
}
